import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle,
  Send,
  Trophy,
  MapPin,
  Building,
  Users,
  BarChart3,
  Package,
  Target,
  Mail,
  Star,
  Globe,
  Edit3,
  Factory,
  CreditCard,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import { useOnboardingStore } from '../../../stores/onboardingStore'
import { useToastStore } from '../../../stores/toastStore'
import { getIndustryConfig } from '../industry-configs'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Badge } from '../../../components/ui/Badge'
import { HelpTooltip } from '../../../components/ui/Tooltip'
import { IntelligenceProfileService } from '../../../services/intelligenceProfileService'
import { CompletionConfirmationDialog } from '../../../components/modals/CompletionConfirmationDialog'
import { cn } from '../../../lib/utils'

export const IntelligenceProfileSummaryScreen: React.FC = () => {
  const { data, updateData, resetData } = useOnboardingStore()
  const { addToast } = useToastStore()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfigDetails, setShowConfigDetails] = useState(false)
  const [showCompletionConfirm, setShowCompletionConfirm] = useState(false)

  if (!data.selectedIndustry) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Trophy className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">Complete Onboarding First</h3>
          <p className="text-gray-500">Please complete the onboarding process to view your intelligence profile.</p>
        </div>
      </div>
    )
  }
  
  const industryConfig = getIndustryConfig(data.selectedIndustry)

  // Auto-submit to orchestrator when step 9 loads with completed payment
  useEffect(() => {
    // Simple flag-based duplicate prevention (all services are paid now)
    if (data.orchestratorSubmitted) {
      console.log('✅ Profile already submitted to orchestrator, skipping')
      return
    }

    const shouldSubmit = (
      data.selectedIndustry &&                           // Has industry
      data.selectedService?.id &&                        // Has service
      data.paymentInfo?.paymentStatus === 'succeeded' && // Payment completed
      data.paymentInfo?.paymentIntentId                  // All paid services have payment intent ID
    )

    if (shouldSubmit) {
      console.log('🚀 Submitting to orchestrator for payment:', data.paymentInfo.paymentIntentId)

      IntelligenceProfileService.processCompletion(data, industryConfig)
        .then((result) => {
          console.log('✅ Orchestrator submission successful:', result.profile.profileId)
          // Mark as submitted to prevent duplicates on refresh
          updateData('orchestratorSubmitted', true)
        })
        .catch((error) => {
          console.error('❌ Orchestrator submission failed:', error)
          // Don't block user experience on orchestrator errors
        })
    }
  }, []) // Run once on component mount

  const handleCompleteSetupClick = () => {
    console.log('🔘 Complete Setup button clicked - showing confirmation modal')
    setShowCompletionConfirm(true)
  }

  const handleCompleteSetup = async () => {
    console.log('✅ Confirmation accepted - proceeding with completion')
    setShowCompletionConfirm(false)
    setIsSubmitting(true)
    
    try {
      // Show completion toast
      addToast({
        type: 'success',
        title: 'Setup Complete!',
        description: 'Your intelligence configuration has been completed successfully.',
        duration: 2000
      })
      
      // Clear the onboarding state AND localStorage to prevent re-entry loop
      resetData()
      localStorage.removeItem('strategic-intelligence-onboarding-data')
      
      // Navigate to landing page after a short delay
      setTimeout(() => {
        navigate('/')
      }, 1500)
      
    } catch (error) {
      console.error('Completion process failed:', error)
      
      // Show error toast
      addToast({
        type: 'error',
        title: 'Completion Error',
        description: 'An error occurred while completing the setup. Please try again.',
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Calculate completion statistics - UPDATED to include Data Integration
  const getProfileStats = () => {
    const hasBusinessProfile = data.contactInfo.companyName && data.businessProfile.businessType
    const hasRevenueStreams = data.revenueStreams.length > 0
    const hasSuppliers = data.suppliers.length > 0
    const hasCompetitive = data.competitiveProfile.serviceArea.city && data.competitiveProfile.serviceArea.state
    const hasSelectedService = data.selectedService?.id
    const hasDelivery = data.contactInfo.email
    
    const completedSections = [
      hasBusinessProfile, 
      hasRevenueStreams, 
      hasSuppliers, 
      hasCompetitive, 
      hasSelectedService,
      hasDelivery
    ].filter(Boolean).length
    
    const totalSections = 6 // Business, Revenue, Supply Chain, Competitive, Service, Delivery
    
    return {
      completedSections,
      totalSections,
      completionPercentage: Math.round((completedSections / totalSections) * 100),
      isComplete: completedSections === totalSections,
      hasSelectedService
    }
  }

  const stats = getProfileStats()

  // Get estimated value based on configuration completeness
  const getEstimatedValue = () => {
    const baseValue = stats.completionPercentage * 50 // Base on completion
    const revenueBonus = data.revenueStreams.length * 100
    const supplierBonus = data.suppliers.length * 75
    const competitorBonus = data.competitiveProfile.knownCompetitors.length * 25
    const serviceBonus = data.selectedService ? 150 : 0
    
    return Math.round(baseValue + revenueBonus + supplierBonus + competitorBonus + serviceBonus)
  }

  const estimatedValue = getEstimatedValue()

  const formatFrequency = (freq: string) => {
    switch (freq) {
      case 'monthly': return 'Monthly Reports'
      case 'bi-weekly': return 'Bi-weekly Reports'
      case 'weekly-plus-monthly': return 'Weekly Alerts + Monthly Deep Dive'
      default: return 'Not configured'
    }
  }

  const getDeliveryFormats = () => {
    const formatMap: { [key: string]: string } = {
      'email-pdf': 'Email PDF',
      'web-dashboard': 'Web Dashboard',
      'email-alerts': 'Email Alerts',
      'phone-briefing': 'Phone Briefing'
    }
    return data.selectedService?.deliveryFormats?.map(f => formatMap[f] || f).join(', ') || 'Email Report'
  }

  // Format industry name for display
  const getIndustryDisplayName = () => {
    switch (data.selectedIndustry) {
      case 'automotive-services': return 'Automotive Services'
      case 'retail': return 'Retail'
      case 'healthcare': return 'Healthcare'
      case 'manufacturing': return 'Manufacturing'
      case 'hospitality': return 'Hospitality'
      case 'real-estate': return 'Real Estate'
      case 'technology': return 'Technology'
      case 'financial-services': return 'Financial Services'
      default: return data.selectedIndustry.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  // Get tier badge configuration for visual tier badges
  const getTierBadgeConfig = (tierLevel?: string) => {
    switch (tierLevel) {
      case 'quick':
        return { label: 'QUICK', bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500' }
      case 'standard':
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
      case 'comprehensive':
        return { label: 'COMPREHENSIVE', bgClass: 'bg-gradient-to-r from-purple-500 to-pink-500' }
      case 'enterprise':
        return { label: 'ENTERPRISE', bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500' }
      default:
        return { label: 'STANDARD', bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500' }
    }
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full">
          <div className="w-8 h-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
            <Trophy className="w-4 h-4 text-white" />
          </div>
          <span className="font-semibold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">
            Configuration Complete
          </span>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-3">
          Your Strategic Intelligence Profile
          <HelpTooltip text="Review your complete intelligence configuration before activation. This summary shows all the areas we'll monitor and how insights will be delivered to optimize your business decisions." />
        </h2>
        <p className="text-gray-600 max-w-4xl mx-auto leading-relaxed">
          Congratulations! Your personalized intelligence monitoring system is configured and ready 
          to deliver actionable insights tailored to your business needs.
        </p>

      </div>

      {/* What's Next Section */}
      <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-green-800 mb-2">What's Next</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-700">
                    <strong>Processing:</strong> Your intelligence report will begin processing immediately after payment
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-700">
                    <strong>Analysis:</strong> Our AI systems will analyze your business data and market intelligence
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-green-700">
                    <strong>Delivery:</strong> Complete report delivered to your email within 4 hours
                  </span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-white/60 rounded-lg border border-green-200/80">
                <div className="flex items-center gap-2 text-sm text-green-800 font-medium">
                  <Shield className="w-4 h-4" />
                  All reports include multiple export formats and comprehensive analysis
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Information - Moved to top */}
      {data.paymentInfo && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Details Card */}
          <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors lg:col-span-2">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-4 h-4 text-gray-600" />
                </div>
                Payment Details
                {data.paymentInfo.paymentStatus === 'succeeded' && (
                  <Badge className="bg-gray-600 text-white border-0 ml-auto">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Payment Confirmed
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Payment Status */}
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-800 mb-2">Payment Status</div>
                    <div className="flex items-center gap-2">
                      {data.paymentInfo.paymentStatus === 'succeeded' ? (
                        <>
                          <div className="w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold text-gray-900">Payment Successful</span>
                        </>
                      ) : (
                        <span className="text-gray-600">Pending</span>
                      )}
                    </div>
                    {data.paymentInfo.processedAt && (
                      <div className="text-sm text-gray-600 mt-2">
                        Processed: {new Date(data.paymentInfo.processedAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Amount */}
                  {data.paymentInfo.amount !== undefined && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-800 mb-1">Amount Paid</div>
                      <div className="text-xl font-bold text-gray-900">
                        ${Number(data.paymentInfo.amount).toFixed(2)} {data.paymentInfo.currency?.toUpperCase()}
                      </div>
                      {data.paymentInfo.amount === 0 && (
                        <div className="text-sm text-gray-600 mt-1">Free Service - No payment required</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Billing Information */}
                {data.paymentInfo.billingDetails && (
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-800 mb-2">Billing Information</div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="text-gray-600">Name:</span>
                          <span className="text-gray-900 font-medium ml-2">
                            {data.paymentInfo.billingDetails.cardholderName}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Email:</span>
                          <span className="text-gray-900 font-medium ml-2">
                            {data.paymentInfo.billingDetails.email}
                          </span>
                        </div>
                        {data.paymentInfo.billingDetails.companyName && (
                          <div>
                            <span className="text-gray-600">Company:</span>
                            <span className="text-gray-900 font-medium ml-2">
                              {data.paymentInfo.billingDetails.companyName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Method */}
                {data.paymentInfo.paymentMethod && (
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-800 mb-2">Payment Method</div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white border border-gray-300 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 capitalize">
                            {data.paymentInfo.paymentMethod.brand || 'Card'}
                          </div>
                          {data.paymentInfo.paymentMethod.last4 && (
                            <div className="text-sm text-gray-700">
                              •••• {data.paymentInfo.paymentMethod.last4}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Security Badge */}
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Shield className="w-4 h-4" />
                        <span className="font-medium">Secure Payment Processed</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transaction ID */}
                {data.paymentInfo.paymentIntentId && (
                  <div className="lg:col-span-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs text-gray-600">
                        Transaction ID: {data.paymentInfo.paymentIntentId}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration Details - Collapsible Section */}
      <div className="space-y-6">
        {/* Collapsible Toggle */}
        <div className="text-center">
          <Button
            variant="outline"
            onClick={() => setShowConfigDetails(!showConfigDetails)}
            className="flex items-center gap-2 mx-auto px-6 py-3 text-gray-700 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors"
          >
            {showConfigDetails ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Hide Configuration Details
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show Configuration Details
              </>
            )}
          </Button>
        </div>

        {/* Collapsible Content */}
        {showConfigDetails && (
          <div className="space-y-6">
            {/* Industry & Company Overview */}
      <Card className="border-2 border-gray-200 bg-gradient-to-r from-gray-50 to-gray-50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-3 text-xl">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            Company & Industry Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Industry Badge - NEW ADDITION */}
          <div className="mb-6 p-4 bg-white rounded-xl border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl flex items-center justify-center">
                <Factory className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-sm font-medium text-gray-700">Industry Sector</div>
                <div className="text-xl font-bold text-gray-900">{getIndustryDisplayName()}</div>
                <div className="text-sm text-gray-600 mt-1">
                  Intelligence configured for {industryConfig.name} industry standards
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Building className="w-4 h-4" />
                <span className="font-medium">Company</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900">{data.contactInfo.companyName || 'Not specified'}</div>
                <div className="text-sm text-gray-700">
                  {industryConfig.businessTypes.find(t => t.id === data.businessProfile.businessType)?.name || 'Business type not selected'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin className="w-4 h-4" />
                <span className="font-medium">Location</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {data.businessProfile.location.city && data.businessProfile.location.state 
                    ? `${data.businessProfile.location.city}, ${data.businessProfile.location.state}`
                    : 'Not specified'}
                </div>
                <div className="text-sm text-gray-700">
                  {data.businessProfile.location.serviceRadius 
                    ? `${data.businessProfile.location.serviceRadius} service radius`
                    : 'Service area not defined'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-4 h-4" />
                <span className="font-medium">Scale</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900">
                  {data.businessProfile.companySize || 'Not specified'}
                </div>
                <div className="text-sm text-gray-700">
                  {data.businessProfile.annualRevenue || 'Revenue not specified'}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4" />
                <span className="font-medium">Contact</span>
              </div>
              <div>
                <div className="font-semibold text-gray-900">{data.contactInfo.contactPerson || 'Not specified'}</div>
                <div className="text-sm text-gray-700 truncate">{data.contactInfo.email || 'Email not provided'}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Intelligence Configuration Overview - First Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Intelligence */}
        <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-4 h-4 text-gray-600" />
              </div>
              Revenue Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.revenueStreams.length > 0 ? (
              <div className="space-y-3">
                {data.revenueStreams.map((stream, index) => {
                  const streamConfig = industryConfig.revenueStreamTypes.find(t => t.id === stream.type)
                  return (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-800">{streamConfig?.name}</div>
                        <div className="text-sm text-gray-600">
                          {stream.partners && stream.partners.length > 0 && `${stream.partners.length} partners monitored`}
                        </div>
                      </div>
                      <div className="font-bold text-gray-700">{stream.percentage}%</div>
                    </div>
                  )
                })}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <div className="text-sm font-medium text-gray-800">
                    ✓ Monitoring {data.revenueStreams.length} revenue stream{data.revenueStreams.length > 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div>No revenue streams configured</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Supply Chain Intelligence */}
        <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-gray-600" />
              </div>
              Supply Chain Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.suppliers.length > 0 ? (
              <div className="space-y-3">
                {data.suppliers.map((supplier, index) => {
                  const categoryConfig = industryConfig.supplierCategories.find(c => c.id === supplier.category)
                  return (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-800">{categoryConfig?.name}</div>
                      <div className="text-sm text-gray-600">
                        Primary: {supplier.primarySupplier || 'TBD'}
                        {supplier.monthlySpend && ` • ${supplier.monthlySpend}`}
                      </div>
                      {supplier.alternativeSuppliers && supplier.alternativeSuppliers.length > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          +{supplier.alternativeSuppliers.length} backup options
                        </div>
                      )}
                    </div>
                  )
                })}
                <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                  <div className="text-sm font-medium text-gray-800">
                    ✓ Monitoring {data.suppliers.length} supplier categor{data.suppliers.length > 1 ? 'ies' : 'y'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <div>No suppliers configured</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Intelligence Configuration Overview - Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Competitive Intelligence */}
        <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-gray-600" />
              </div>
              Competitive Intelligence
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-800 mb-2">Market Area</div>
                <div className="text-sm text-gray-600">
                  {data.competitiveProfile.serviceArea.city && data.competitiveProfile.serviceArea.state 
                    ? `${data.competitiveProfile.serviceArea.city}, ${data.competitiveProfile.serviceArea.state}`
                    : 'Not specified'}
                  {data.competitiveProfile.serviceArea.radius && (
                    <span> • {data.competitiveProfile.serviceArea.radius} radius</span>
                  )}
                </div>
              </div>

              {data.competitiveProfile.competitiveAdvantages.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800 mb-2">Key Advantages</div>
                  <div className="text-sm text-gray-600">
                    {data.competitiveProfile.competitiveAdvantages.slice(0, 3).join(', ')}
                    {data.competitiveProfile.competitiveAdvantages.length > 3 && 
                      ` +${data.competitiveProfile.competitiveAdvantages.length - 3} more`}
                  </div>
                </div>
              )}

              {data.competitiveProfile.knownCompetitors.length > 0 && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800 mb-2">Direct Competitors</div>
                  <div className="text-sm text-gray-600">
                    Monitoring {data.competitiveProfile.knownCompetitors.length} specific competitor{data.competitiveProfile.knownCompetitors.length > 1 ? 's' : ''}
                  </div>
                </div>
              )}

              <div className="p-3 bg-gray-100 rounded-lg">
                <div className="text-sm font-medium text-gray-800">
                  ✓ Competitive monitoring active
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Service Selection - Updated for Phase 4 */}
        <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Package className="w-4 h-4 text-gray-600" />
              </div>
              Service Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.selectedService ? (
                <>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="font-medium text-gray-800 mb-2">Selected Service</div>
                    <div className="text-lg font-semibold text-gray-900 mb-1">
                      {data.selectedService.name}
                    </div>
                    <div className="mb-2">
                      <Badge className={`${getTierBadgeConfig(data.selectedService.tierLevel).bgClass} text-white border-0`}>
                        {getTierBadgeConfig(data.selectedService.tierLevel).label} TIER
                      </Badge>
                    </div>
                    <div className="text-xl font-bold text-gray-800">
                      ${Number(data.selectedService.price).toFixed(2)}{data.selectedService.billingType === 'one-time' ? ' one-time' : '/month'}
                    </div>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <div className="text-sm font-medium text-gray-800 mb-2">
                      Delivery Formats:
                    </div>
                    <div className="space-y-1">
                      {data.selectedService.deliveryFormats?.map((format, index) => (
                        <div key={index} className="text-xs text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {format}
                        </div>
                      )) || (
                        <div className="text-xs text-gray-600">Email Report</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <div>No service selected</div>
                  <div className="text-sm text-gray-400 mt-1">Please select a service plan</div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Delivery Details */}
        <Card className="border-2 border-gray-200 hover:border-gray-300 transition-colors">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <Mail className="w-4 h-4 text-gray-600" />
              </div>
              Delivery Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-800 mb-2">Delivery Email Address</div>
                <div className="text-gray-900 font-semibold">
                  {data.contactInfo.email || 'Not specified'}
                </div>
                <div className="text-sm text-gray-600 mt-2">
                  Your intelligence report will be delivered to this email address within 4 hours
                </div>
              </div>
              
              {data.selectedService && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-gray-800 mb-2">Delivery Format</div>
                  <div className="text-sm text-gray-600">
                    {getDeliveryFormats()}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
          </div>
        )}
      </div>

      {/* Completion Section */}
      <div className="text-center">
        <Button
          onClick={handleCompleteSetupClick}
          disabled={isSubmitting}
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 text-lg font-semibold flex items-center gap-3 mx-auto"
          size="lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Completing...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              Complete Setup
            </>
          )}
        </Button>
      </div>

      {/* Completion Confirmation Dialog */}
      <CompletionConfirmationDialog
        isOpen={showCompletionConfirm}
        onConfirm={handleCompleteSetup}
        onCancel={() => setShowCompletionConfirm(false)}
      />

    </div>
  )
}